import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

type BillingPeriod = 'monthly' | 'annual' | 'lifetime';

interface CheckoutSessionData {
  sessionId: string;
  url: string;
}

export function useStripeCheckout() {
  const [isProcessing, setIsProcessing] = useState(false);

  const createCheckoutSession = useMutation({
    mutationFn: async ({ 
      planId, 
      billingPeriod 
    }: { 
      planId: string; 
      billingPeriod: BillingPeriod;
    }) => {
      const { data, error } = await supabase.functions.invoke<CheckoutSessionData>(
        'create-checkout-session',
        {
          body: { planId, billingPeriod },
        }
      );

      if (error) {
        console.error('Edge function error:', error);
        throw error;
      }
      if (!data) throw new Error('No data returned from checkout session');
      if (!data.url) throw new Error('No checkout URL in response');

      return data;
    },
    onSuccess: (data) => {
      console.log('=== CHECKOUT DEBUG ===');
      console.log('Session ID:', data.sessionId);
      console.log('Checkout URL:', data.url);
      console.log('Full response:', data);
      console.log('=====================');
      
      toast.dismiss();
      
      if (!data.url) {
        toast.error('Erro: URL de checkout não recebida', {
          description: `Session ID: ${data.sessionId}`,
          duration: 10000,
        });
        setIsProcessing(false);
        return;
      }
      
      toast.success('Abrindo página de pagamento...');
      
      // Abrir em nova aba (melhor para ambiente de teste)
      const checkoutWindow = window.open(data.url, '_blank');
      
      if (!checkoutWindow) {
        toast.error('Bloqueio de popup detectado', {
          description: 'Por favor, permita popups para este site',
          duration: 5000,
        });
      }
      
      // Resetar estado após abrir
      setTimeout(() => {
        setIsProcessing(false);
      }, 1000);
    },
    onError: (error) => {
      console.error('Checkout error:', error);
      toast.dismiss();
      toast.error('Erro ao criar sessão de pagamento', {
        description: error instanceof Error ? error.message : 'Tente novamente mais tarde',
      });
      setIsProcessing(false);
    },
  });

  const initiateCheckout = async (planId: string, billingPeriod: BillingPeriod) => {
    try {
      setIsProcessing(true);
      toast.loading('Preparando pagamento...');
      await createCheckoutSession.mutateAsync({ planId, billingPeriod });
    } catch (error) {
      // Error already handled in onError
      console.error('Checkout initiation failed:', error);
    }
  };

  return {
    initiateCheckout,
    isProcessing,
    isLoading: createCheckoutSession.isPending,
  };
}
